import { useQuery } from '@tanstack/react-query';
import api from '../../../api/axiosInstance';
import { lastMonth, TimeRangeDropdownOptions } from '../../../const/DashboardTypes';
import { useEffect, useState } from 'react';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { toStartOfDayISO, toEndOfDayISO } from '../utils/dashboardDateUtils';

export type PipelineSuccessRate = { status: string; ticket_count: number; percentage: number };

export type GetPipelineSuccessStatsParams = {
  timeRangeText?: string;
  startDate?: string;
  endDate?: string;
};

export async function getPipelines(params?: GetPipelineSuccessStatsParams): Promise<PipelineSuccessRate[]> {
  try {
    const baseUrl = '/api/dashboard-metric/success-rate';
    let url = baseUrl;

    const { timeRangeText, startDate, endDate } = params ?? {};

    if (startDate != null && endDate != null) {
      const startISO = toStartOfDayISO(startDate);
      const endISO = toEndOfDayISO(endDate);
      url = `${baseUrl}?startDate=${encodeURIComponent(startISO)}&endDate=${encodeURIComponent(endISO)}`;
    } else if (timeRangeText) {
      const filterText = timeRangeText === TimeRangeDropdownOptions.CURRENT_MONTH ? lastMonth : timeRangeText.toLowerCase();
      url = `${baseUrl}?global=${encodeURIComponent(filterText)}`;
    }

    const { data } = await api.get(url);
    return data ? data : [];
  } catch (error) {
    console.error('Error fetching success rate:', error);
    throw error;
  }
}

export function useGetPipelineSuccessStats(timeRangeTextOrParams?: string | GetPipelineSuccessStatsParams) {
  const params: GetPipelineSuccessStatsParams =
    typeof timeRangeTextOrParams === 'string' ? { timeRangeText: timeRangeTextOrParams } : (timeRangeTextOrParams ?? {});

  const { timeRangeText, startDate, endDate } = params;
  const { addNotifications } = useSwobNotifications();
  const [hasError, setHasError] = useState(false);

  const isEndDateToday = endDate != null && endDate === new Date().toISOString().slice(0, 10);
  const isCurrentMonthSelected = timeRangeText === TimeRangeDropdownOptions.CURRENT_MONTH;
  const shouldRefetch = !hasError && (isCurrentMonthSelected || (startDate != null && endDate != null && isEndDateToday));

  const queryKey =
    startDate != null && endDate != null ? ['success-rate-pipelines', startDate, endDate] : ['success-rate-pipelines', timeRangeText];

  const query = useQuery<PipelineSuccessRate[]>({
    queryKey,
    queryFn: async () => getPipelines(params),
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: shouldRefetch ? 60000 : false
  });

  useEffect(() => {
    if (query.error && !hasError) {
      setHasError(true);
      addNotifications([
        {
          title: 'Error - Dashboard Metrics',
          icon: 'icon-triangle-warning',
          description: 'An error occurred while fetching the success rate.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.error, hasError]);
  return query;
}
